import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  GetMessageWithIntl,
  MessageProps,
  Toast,
  error,
} from '~/shared/components';
import {
  ConfirmationDialog,
  PageNavigation,
  ListColumnSetting,
  SimpleListView,
  Textbox,
  Textarea,
  Checkbox,
  LatestUpdateInfo,
} from '~/shared/components/ui';
import { CaptionButton, IconButton } from '~/shared/components/ui/Button';
import { mtechnavi, sharelib } from '~/shared/libs/clientsdk';
import {
  FullMethodName_ListBusinessUnitBranchs,
  FullMethodName_ListBusinessUnitFacilitiess,
  PageState,
  ViewId,
  getExceptionMessage,
  getWorkerExceptionMessage,
  includeInputValidateError,
  getProgramOptionFilterboxData,
  convertLongToString,
  convertStringToLong,
  getProgramOptionFilterboxDataWithSystemName,
  convertNameOptionToFilterboxItem,
} from '~/shared/utils';
import { Accordion } from '~/shared/components/ui/Accordion';
import { PresetItem } from '~/shared/services';
import {
  Filterbox,
  FilterboxItem,
  DataFilterbox,
  DataFilterboxItem,
} from '~/shared/components/ui/Filterbox';
import './FacilitiesInput.css';
import { CategorizedList } from '~/shared/components/ui/CategorizedCheckList';

import {
  convertToCategorizedList,
  BusinessUnitAttributeCategory,
  BusinessUnitAttributeItem,
  BusinessUnitAttributes,
  MaterialSkillRelationItem,
  generateMaterialSkillRelationItems,
} from 'tenant/src/company/pages/converter';

import { ConditionDetail, ConditionItem } from 'tenant/src/company/pages/util';

import {
  MaterialTreeDialog,
  MaterialTreeDialogRef,
} from 'tenant/src/company/pages/CompanySearch/parts/MaterialTreeDialog';

import { RadioSelectDialog } from '~/shared/components/ui/Dialog/RadioSelectDialog';
import { RadioItem } from '~/shared/components/parts/Radio/Radio';
import { validateOnlyNumberWithComma } from '~/shared/components/parts/validator';
import { CommaTypeFormatter } from '~/shared/components/parts/formatter';
import { CheckItem } from '~/shared/components/ui/CategorizedCheckList/util';
import { useLoading } from '~/shared/contexts/LoadingProvider';

type MaterialTreeType = 'processingSkil' | 'material';

interface MergedItem extends BusinessUnitAttributeItem {
  // 親の情報を「親ID: 名前」の形式で動的に持つ場合
  [key: `cat_${string}`]: string | undefined;
  itemId: string;
  displayName: string;
}

interface MaterialTreeDialogInfo {
  type?: MaterialTreeType;
  editIndex?: number;
  relationIds?: mtechnavi.api.company.ISkillRelation[];
}

interface MaterialSkillRelation {
  itemId: string;
  materialItemId?: string;
  material?: string;
  technologyItemId?: string;
  technology?: string;
}

const VIEW_ID: ViewId = 'OCP_FACILITIES_INPUT';

const materialSkillListColumns: ListColumnSetting[] = [
  {
    header: { viewId: VIEW_ID, id: 'material' },
    propertyName: 'material',
    width: '45%',
  },
  {
    header: { viewId: VIEW_ID, id: 'skill' },
    propertyName: 'technology',
    width: '45%',
  },
];

export const FacilitiesInput = () => {
  const sourcePageInfo = useRef((useLocation().state as PageState) ?? []);
  const actionType = sourcePageInfo.current.actionType ?? 'add';
  const intl = useIntl();
  const navi = useNavigate();
  const backPageUrl = '/facilities-list';
  const { showLoading, hideLoading } = useLoading();

  type BusinessUnitFacilities = mtechnavi.api.company.IBusinessUnitFacilities;
  type SkillRelation = mtechnavi.api.company.ISkillRelation;
  type FacilitiesBasic =
    mtechnavi.api.company.IGeneratedBusinessUnitFacilitiesBasic;
  type FacilitiesSkills =
    mtechnavi.api.company.IGeneratedBusinessUnitFacilitiesSkills;

  // ラベル
  const labelSave = GetMessageWithIntl(intl, {
    id: 'save',
  });
  const labelGenerate = GetMessageWithIntl(intl, {
    id: 'generateButton',
    prefixId: VIEW_ID,
  });
  const labelAddMaterialSkill = GetMessageWithIntl(intl, {
    id: 'addMaterialSkill',
    prefixId: VIEW_ID,
  });

  // メッセージ
  const viewMessageBack: MessageProps = {
    id: 'confirmationDialogMessageBack',
    prefixId: VIEW_ID,
  };
  const viewMessageSave: MessageProps = {
    id: 'C0000001',
    value: {
      $1: GetMessageWithIntl(intl, { id: 'save' }),
    },
  };
  const viewMessageGenerate: MessageProps = {
    id: 'setGenerateResult',
    prefixId: VIEW_ID,
  };
  const viewMessageListDeleteAll: MessageProps = {
    id: 'listDeleteAll',
    prefixId: VIEW_ID,
  };
  const viewMessageListDelete: MessageProps = {
    id: 'listDelete',
    prefixId: VIEW_ID,
  };
  const viewMessageDelete: MessageProps = {
    id: 'C0000001',
    value: {
      $1: GetMessageWithIntl(intl, { id: 'delete' }),
    },
  };

  const branchItemType = {
    value: 'businessUnitBranchId',
    displayName: 'displayName',
  };

  const FACILITY_CATEGORY_NAME = 'A4000003';
  const ACCURACY_NAME = 'A4000004';
  const SIZE_NAME = 'A4000014';
  const PROCESSING_ENVIROMENTS_NAME = 'A4000013';

  // 分類で分割するためのカテゴリID
  const PROCESSING_ATC_ID = 'ATC-48'; // 加工技術
  const MATERIAL_ATC_ID = 'ATC-135'; // 素材

  const materialSkillValues = ['material', 'skill'];
  const attributeCategoryRadioItems: RadioItem[] = [
    {
      displayName: GetMessageWithIntl(intl, {
        id: 'radio_material',
        prefixId: VIEW_ID,
      }),
      value: materialSkillValues[0],
    },
    {
      displayName: GetMessageWithIntl(intl, {
        id: 'radio_skil',
        prefixId: VIEW_ID,
      }),
      value: materialSkillValues[1],
    },
  ];

  const [facilityCategorys, accuracys, sizeDatas, processingEnvironmentDatas] =
    useMemo(() => {
      const facilityCategorys = getProgramOptionFilterboxData(
        FACILITY_CATEGORY_NAME
      );

      const accuracys = getProgramOptionFilterboxData(ACCURACY_NAME);
      const sizeDatas = getProgramOptionFilterboxData(SIZE_NAME);

      const processingEnvironmentDatas = getProgramOptionFilterboxData(
        PROCESSING_ENVIROMENTS_NAME
      );

      return [
        facilityCategorys,
        accuracys,
        sizeDatas,
        processingEnvironmentDatas,
      ];
    }, []);

  const myCompanyInfo = useRef<mtechnavi.api.company.ICompany>();
  const businessUnitAttributesInfo = useRef<BusinessUnitAttributes>();
  const formAreaRef = useRef(null);
  const materialTreeDialogRef = useRef<MaterialTreeDialogRef>(null);
  const businessUnitFacilitiesData =
    useRef<mtechnavi.api.company.IBusinessUnitFacilities>({});
  const businessUnitBranchsData = useRef<
    mtechnavi.api.company.IBusinessUnitBranch[]
  >([]);
  const footerRef = useRef<HTMLDivElement>(null);

  // 編集制御
  const [editInfo, setEditInfo] = useState<MaterialTreeDialogInfo>({});

  // preset
  const [preset, setPreset] = useState<PresetItem>({ name: '' });

  const [materialSkillRelationListItems, setMaterialSkillRelationListItems] =
    useState<MaterialSkillRelation[]>([]);

  const [skillRelationItems, setSkillRelationItems] = useState<SkillRelation[]>(
    []
  );

  const [materialSkillRelationTableItems, setMaterialSkillRelationTableItems] =
    useState<MaterialSkillRelationItem[]>([]);

  const [lastUpdateInfo, setLastUpdateInfo] = useState<LatestUpdateInfo>({
    isVisibleUpdateInfo: false,
  });

  // 現行の設定
  // 設備拠点（現行の設定）
  const [nowBusinessUnitBranch, setNowBusinessUnitBranch] = useState<
    FilterboxItem[]
  >([]);
  // 設備分類（現行の設定）
  const [nowCategorys, setNowCategorys] = useState<DataFilterboxItem[]>([]);
  // メーカー（現行の設定）
  const [nowManufacture, setNowManufacture] = useState('');
  // 型番（現行の設定）
  const [nowModelNumber, setNowModelNumber] = useState('');
  // 台数（現行の設定）
  const [nowAssetsAmount, setNowAssetsAmount] = useState('');
  // 精度（現行の設定）
  const [nowAccuracy, setNowAccuracy] = useState<DataFilterboxItem[]>([]);
  // 加工トン数（トン）（現行の設定）
  const [nowProcessableTon, setNowProcessableTon] = useState('');
  // 加工サイズ 幅X（mm）（現行の設定）
  const [nowSizeX, setNowSizeX] = useState('');
  // 加工サイズ 奥行きY（mm）（現行の設定）
  const [nowSizeY, setNowSizeY] = useState('');
  // 加工サイズ（現行の設定）
  const [nowSize, setNowSize] = useState<DataFilterboxItem[]>([]);
  // 加工サイズ（mm）（現行の設定）
  const [nowSizeMM, setNowSizeMM] = useState('');
  // 加工環境（現行の設定）
  const [nowProcessingEnvironments, setNowProcessingEnvironments] = useState<
    DataFilterboxItem[]
  >([]);
  // 専用機（現行の設定）
  const [nowDedicatedMachine, setNowDedicatedMachine] = useState(false);
  // 備考（現行の設定）
  const [nowRemarks, setNowRemarks] = useState('');

  // 更新内容（生成結果）
  // 設備拠点
  const [businessUnitBranch, setBusinessUnitBranch] = useState<FilterboxItem[]>(
    []
  );
  // 設備分類
  const [categorys, setCategorys] = useState<DataFilterboxItem[]>([]);
  // メーカー
  const [manufacture, setManufacture] = useState('');
  // 型番
  const [modelNumber, setModelNumber] = useState('');
  // 台数
  const [assetsAmount, setAssetsAmount] = useState('');
  // 精度
  const [accuracy, setAccuracy] = useState<DataFilterboxItem[]>([]);
  // 加工トン数（トン）
  const [processableTon, setProcessableTon] = useState('');
  // 加工サイズ 幅X（mm）
  const [sizeX, setSizeX] = useState('');
  // 加工サイズconst 奥行きY（mm）
  const [sizeY, setSizeY] = useState('');
  // 加工サイズ
  const [size, setSize] = useState<DataFilterboxItem[]>([]);
  // 加工サイズ（mm）
  const [sizeMM, setSizeMM] = useState('');
  // 加工環境
  const [processingEnvironments, setProcessingEnvironments] = useState<
    DataFilterboxItem[]
  >([]);
  // 専用機
  const [dedicatedMachine, setDedicatedMachine] = useState(false);
  // 備考
  const [remarks, setRemarks] = useState('');
  // 属性分類選択ダイアログ
  const [isShowMaterialSelectDialog, setShowMaterialSelectDialog] =
    useState(false);

  // 生成して登録
  // プロンプト
  const [prompt, setPrompt] = useState('');

  // 確認ダイアログ（戻る）
  const [isBackConfirmOpen, setBackConfirmOpen] = useState(false);

  // 確認ダイアログ（生成）
  const [isGenerateConfirmOpen, setGenerateConfirmOpen] = useState(false);

  // 確認ダイアログ（保存）
  const [isSaveConfirmOpen, setSaveConfirmOpen] = useState(false);
  // 確認ダイアログ（削除）
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // 確認ダイアログ（明細削除）
  const [isListDeleteConfirmOpen, setListDeleteConfirmOpen] = useState(false);

  // 確認ダイアログ（ヘッダ削除）
  const [isListDeleteAllConfirmOpen, setListDeleteAllConfirmOpen] =
    useState(false);

  const [deleteTargetId, setDeleteTargetId] = useState('');

  const [processingSkillData, setProcessingSkillData] =
    useState<CategorizedList>([]);
  const [materialData, setMaterialData] = useState<CategorizedList>([]);
  const [technologyTreeData, setTechnologyTreeData] = useState<
    BusinessUnitAttributeCategory[]
  >([]);
  const [materialTreeData, setMaterialTreeData] = useState<
    BusinessUnitAttributeCategory[]
  >([]);

  const handleChangeDedicatedMachine = (selection: string[]) => {
    const val = !!selection.length;
    setDedicatedMachine(val);
  };

  // 条件の追加・編集・削除
  const handleMaterialCheckAdd = (
    type: MaterialTreeType,
    treeData: CategorizedList,
    focusId?: string
  ) => {
    setEditInfo({ type });
    materialTreeDialogRef.current?.open(treeData, undefined, focusId);
  };

  // 条件の追加・編集・削除
  const handleMaterialCheckEdit = (
    type: MaterialTreeType,
    treeData: CategorizedList,
    editIndex: number,
    relationIds: SkillRelation[],
    editData: ConditionDetail
  ) => {
    setEditInfo({ type, editIndex, relationIds });
    materialTreeDialogRef.current?.open(treeData, editData);
  };

  const isInputError = (): boolean => {
    // 材料・技術のどっちかが登録されていないときはエラーを表示
    let isSetItem = true;
    skillRelationItems.map((item) => {
      if (!item.materialItemId || !item.technologyItemId) {
        isSetItem = false;
      }
    });
    if (!isSetItem) {
      error([GetMessageWithIntl(intl, { id: 'E0000191' })]);
      return true;
    }

    return includeInputValidateError(document, intl, []);
  };

  const handleDelete = () => {
    setDeleteTargetId('');
    setDeleteConfirmOpen(false);
  };

  const handleRemoveConfirm = (item: MaterialSkillRelation) => {
    setDeleteTargetId(item.itemId);
    setListDeleteConfirmOpen(true);
  };

  const handleRemove = () => {
    setMaterialSkillRelationListItems(
      materialSkillRelationListItems.filter((v) => v.itemId !== deleteTargetId)
    );
    setListDeleteConfirmOpen(false);
  };

  const handleAllRemoveConfirmation = () => {
    setListDeleteAllConfirmOpen(true);
  };

  const handleAllRemove = () => {
    setMaterialSkillRelationListItems([]);
    setListDeleteAllConfirmOpen(false);
  };

  const handleSave = async () => {
    showLoading();
    try {
      await saveBusinessUnitFacilities();
      setSaveConfirmOpen(false);
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
      throw err;
    } finally {
      hideLoading();
    }
    backToPreviewPage();
  };

  const getBusinessUnitBranch = async () => {
    return await window.App.services.ui.worker.filter({
      action: 'reload',
      fullMethodName: FullMethodName_ListBusinessUnitBranchs,
      filter: {},
      requestBody: {
        companyIds: [myCompanyInfo.current?.companyId as string],
      },
      sort: [], // ソートは画面項目セット時に行うため不要
    });
  };

  const saveBusinessUnitFacilities = async () => {
    const saveCaterogrys: sharelib.INameOption[] = [];
    categorys.map((v) => {
      const category = window.App.services.ui.getNameOptionWithCode(
        FACILITY_CATEGORY_NAME,
        v.value
      );
      saveCaterogrys.push(category);
    });

    const saveAccuracy =
      accuracy.length > 0
        ? window.App.services.ui.getNameOptionWithCode(
            ACCURACY_NAME,
            accuracy[0].value
          )
        : null;

    const processingEnvironment: sharelib.INameOption[] = [];

    processingEnvironments.map((v) => {
      processingEnvironment.push(
        window.App.services.ui.getNameOptionWithCode(
          PROCESSING_ENVIROMENTS_NAME,
          v.value
        )
      );
    });
    const sizeCode = size.length > 0 ? size[0].value : '';
    const sizeZ = sizeCode === '1' ? convertStringToLong(sizeMM) : null;
    const sizeT = sizeCode === '2' ? convertStringToLong(sizeMM) : null;
    const diameter = sizeCode === '3' ? convertStringToLong(sizeMM) : null;

    const request: mtechnavi.api.company.IBusinessUnitFacilities = {
      businessUnitFacilitiesId:
        actionType !== 'edit'
          ? null
          : businessUnitFacilitiesData.current.businessUnitFacilitiesId,
      companyId: myCompanyInfo.current?.companyId,
      businessUnitBranchId:
        businessUnitBranch.length > 0 ? businessUnitBranch[0].value : '',
      categorys: saveCaterogrys,
      manufacture: manufacture,
      modelNumber: modelNumber,
      assetsAmount: convertStringToLong(assetsAmount),
      accuracy: saveAccuracy,
      dedicatedMachine: dedicatedMachine,
      processableTon: convertStringToLong(processableTon),
      sizeX: convertStringToLong(sizeX),
      sizeY: convertStringToLong(sizeY),
      sizeZ: sizeZ,
      sizeT: sizeT,
      diameter: diameter,
      processingEnvironments: processingEnvironment,
      processableMaterial: null,
      remarks: remarks,
      skillRelations: skillRelationItems,
      sharedProperties: null,
      updatedProperties: null,
      createdAt:
        actionType !== 'edit'
          ? null
          : businessUnitFacilitiesData.current.createdAt,
      updatedAt:
        actionType !== 'edit'
          ? null
          : businessUnitFacilitiesData.current.updatedAt,
      deletedAt: null,
    };

    await window.App.services.ui.worker.apiCall({
      actionName: 'saveFacilities',
      request: request,
    });
  };

  const generateFacilities = async () => {
    let basics: FacilitiesBasic = {};
    let skills: FacilitiesSkills = {};
    // try {
    const basicRequest: mtechnavi.api.company.IGenerateBusinessUnitFacilitiesBasicRequest =
      {
        prompt: prompt,
      };

    const basicRes = window.App.services.ui.worker.apiCall({
      actionName: 'generateBusinessUnitFacilitiesBasic',
      request: basicRequest,
    });
    const skillsRequest: mtechnavi.api.company.IGenerateBusinessUnitFacilitiesSkillsRequest =
      {
        prompt: prompt,
      };

    const skillsRes = window.App.services.ui.worker.apiCall({
      actionName: 'generateBusinessUnitFacilitiesSkills',
      request: skillsRequest,
    });

    const [basicResponse, skillsResponse] = await Promise.allSettled([
      basicRes,
      skillsRes,
    ]);
    if (basicResponse.status === 'fulfilled') {
      basics =
        basicResponse.value.at(0)?.generatedBusinessUnitFacilitiesBasic || {};
    } else {
      error(getExceptionMessage(intl, basicResponse.reason));
    }
    if (skillsResponse.status === 'fulfilled') {
      skills =
        skillsResponse.value.at(0)?.generatedBusinessUnitFacilitiesSkills || {};
    } else {
      error(getExceptionMessage(intl, skillsResponse.reason));
    }

    return [basics, skills];
  };

  const handleGenerate = async () => {
    showLoading();
    try {
      const [basics, skills] = await generateFacilities();

      // 基本情報のセット
      // 更新内容
      const basicGenerateData = basics as FacilitiesBasic;
      const branchData = businessUnitBranchsData.current.filter(
        (v) =>
          v.businessUnitBranchId === basicGenerateData?.businessUnitBranchId
      );
      const branchFilterboxItem: FilterboxItem =
        branchData.length > 0
          ? {
              value: branchData[0].businessUnitBranchId ?? '',
              displayName: branchData[0].displayName ?? '',
            }
          : {
              value: '',
              displayName: '',
            };

      const categoryNameOptionItem =
        (basicGenerateData?.categorys ?? []).length > 0
          ? basicGenerateData?.categorys![0]
          : undefined;
      const categoryFilterboxItem = convertNameOptionToFilterboxItem(
        categoryNameOptionItem
      );
      const accuracyFilterboxItem = convertNameOptionToFilterboxItem(
        basicGenerateData?.accuracy
      );
      let sizeSystemName = '';
      let sizeMm = null;
      if (basicGenerateData?.sizeZ) {
        sizeSystemName = 'B01';
        sizeMm = basicGenerateData?.sizeZ;
      } else if (basicGenerateData?.sizeT) {
        sizeSystemName = 'B02';
        sizeMm = basicGenerateData?.sizeT;
      } else if (basicGenerateData?.diameter) {
        sizeSystemName = 'B03';
        sizeMm = basicGenerateData?.diameter;
      }
      const sizeFilterboxItem = getProgramOptionFilterboxDataWithSystemName(
        SIZE_NAME,
        sizeSystemName
      );
      const processingEnvironmentsFilterboxItem: DataFilterboxItem[] = [];
      (basicGenerateData?.processingEnvironments ?? []).map((v) => {
        const item = convertNameOptionToFilterboxItem(v);
        if (item) {
          processingEnvironmentsFilterboxItem.push(item);
        }
      });

      setBusinessUnitBranch([branchFilterboxItem]);
      setCategorys(categoryFilterboxItem ? [categoryFilterboxItem] : []);
      setManufacture(basicGenerateData?.manufacture ?? '');
      setModelNumber(basicGenerateData?.modelNumber ?? '');
      setAssetsAmount(convertLongToString(basicGenerateData?.assetsAmount));
      setAccuracy(accuracyFilterboxItem ? [accuracyFilterboxItem] : []);
      setProcessableTon(convertLongToString(basicGenerateData?.processableTon));
      setSizeX(convertLongToString(basicGenerateData?.sizeX));
      setSizeY(convertLongToString(basicGenerateData?.sizeY));
      setSize(sizeFilterboxItem);
      setSizeMM(convertLongToString(sizeMm));
      setProcessingEnvironments(processingEnvironmentsFilterboxItem);
      setDedicatedMachine(false);
      setRemarks(basicGenerateData?.remarks ?? '');

      // 技術-材料のセット
      const materialRelation: MaterialSkillRelation[] = [];
      const skillRelations: SkillRelation[] = [];
      const skillItems = skills as FacilitiesSkills;
      (skillItems.skillRelations || []).map((relation) => {
        const materialItemId = relation.materialItemId || '';
        const technologyItemId = relation.technologyItemId || '';
        const materialFlatData = flattenAndMerge(
          materialTreeData,
          materialItemId
        );
        const technologyFlatData = flattenAndMerge(
          technologyTreeData,
          technologyItemId
        );
        materialRelation.push({
          itemId: `${materialItemId}-${technologyItemId}`,
          materialItemId: materialItemId,
          material: materialFlatData?.displayName || '',
          technologyItemId: technologyItemId,
          technology: technologyFlatData?.displayName || '',
        });
        skillRelations.push({
          materialItemId: materialItemId,
          technologyItemId: technologyItemId,
        });
      });
      setMaterialSkillRelationListItems(materialRelation);
    } catch (err) {
      error(getExceptionMessage(intl, err));
      setGenerateConfirmOpen(false);
    } finally {
      hideLoading();
    }

    setGenerateConfirmOpen(false);
  };

  // 戻るページ
  const backToPreviewPage = () => {
    const ids = [] as string[];
    sourcePageInfo.current.ids?.map((v) => ids.push(v));
    const state: PageState = {
      ids: sourcePageInfo.current.beforeStateIds ?? [],
      sourceViewId: VIEW_ID,
      naviFilters: sourcePageInfo.current.naviFilters,
      beforeStateIds: sourcePageInfo.current.beforeStateIds,
      baseViewOption: sourcePageInfo.current.baseViewOption,
      confirmationViewOption: sourcePageInfo.current.confirmationViewOption,
    };
    navi(backPageUrl, { state });
  };

  // 画面データ取得
  useEffect(() => {
    showLoading();
    (async () => {
      try {
        // 自社情報を取得
        myCompanyInfo.current =
          await window.App.services.companyService.getCompany({});

        businessUnitBranchsData.current = (await getBusinessUnitBranch())
          .items as mtechnavi.api.company.IBusinessUnitFacilities[];

        const attributesRawData = await fetch(
          '/assets/conditions/BusinessUnitAttribute.json'
        );
        const attributes =
          (await attributesRawData.json()) as BusinessUnitAttributes;
        businessUnitAttributesInfo.current = attributes;

        const skillTreeData = attributes.children.filter(
          (v) => v.attributeCategoryId === PROCESSING_ATC_ID
        );
        setTechnologyTreeData(skillTreeData);
        const materialTreeData = attributes.children.filter(
          (v) => v.attributeCategoryId === MATERIAL_ATC_ID
        );
        setMaterialTreeData(materialTreeData);

        attributes.children.forEach((item) => {
          const categorizedItem = convertToCategorizedList(item.children);
          switch (item.attributeCategoryId) {
            case PROCESSING_ATC_ID:
              setProcessingSkillData(categorizedItem);
              return;
            case MATERIAL_ATC_ID:
              setMaterialData(categorizedItem);
              return;
            default:
              return;
          }
        });

        // 新規登録モード
        if (actionType === 'add') {
          // 現在の設定
          setNowBusinessUnitBranch([]);
          setNowCategorys([]);
          setNowManufacture('');
          setNowModelNumber('');
          setNowAssetsAmount('');
          setNowAccuracy([]);
          setNowProcessableTon('');
          setNowSizeX('');
          setNowSizeY('');
          setNowSize([]);
          setNowSizeMM('');
          setNowProcessingEnvironments([]);
          setNowDedicatedMachine(false);
          setNowRemarks('');

          // 更新内容
          setBusinessUnitBranch([]);
          setCategorys([]);
          setManufacture('');
          setModelNumber('');
          setAssetsAmount('');
          setAccuracy([]);
          setProcessableTon('');
          setSizeX('');
          setSizeY('');
          setSize([]);
          setSizeMM('');
          setProcessingEnvironments([]);
          setDedicatedMachine(false);
          setRemarks('');

          // 生成結果テーブル
          setMaterialSkillRelationListItems([]);
          setSkillRelationItems([]);
          // 材料-技術テーブル
          setMaterialSkillRelationTableItems([]);
          setLastUpdateInfo({ isVisibleUpdateInfo: false });
        }

        // 編集モード、複写モード
        if (actionType === 'edit' || actionType === 'copy') {
          const listBusinessUnitFacilitiessRes =
            (await window.App.services.ui.worker.filter({
              action: 'query',
              fullMethodName: FullMethodName_ListBusinessUnitFacilitiess,
              filter: {
                businessUnitFacilitiesId: {
                  $eq: sourcePageInfo.current.ids![0] ?? '',
                },
              },
              sort: [],
            })) as mtechnavi.api.company.ListBusinessUnitFacilitiessResponse;

          const businessUnitFacilities = listBusinessUnitFacilitiessRes
            .items[0] as BusinessUnitFacilities;

          businessUnitFacilitiesData.current = businessUnitFacilities;
          const branchData = businessUnitBranchsData.current.filter(
            (v) =>
              v.businessUnitBranchId ===
              businessUnitFacilities.businessUnitBranchId
          );
          const branchFilterboxItem: FilterboxItem =
            branchData.length > 0
              ? {
                  value: branchData[0].businessUnitBranchId ?? '',
                  displayName: branchData[0].displayName ?? '',
                }
              : {
                  value: '',
                  displayName: '',
                };

          const categoryFilterboxItems: FilterboxItem[] = [];
          (businessUnitFacilities.categorys ?? []).map((v) => {
            const categoryFilterboxItem = convertNameOptionToFilterboxItem(v);
            if (categoryFilterboxItem) {
              categoryFilterboxItems.push(categoryFilterboxItem);
            }
          });
          const accuracyFilterboxItem = convertNameOptionToFilterboxItem(
            businessUnitFacilities.accuracy
          );
          let sizeSystemName = '';
          let sizeMm = null;
          if (businessUnitFacilities.sizeZ) {
            sizeSystemName = 'B01';
            sizeMm = businessUnitFacilities.sizeZ;
          } else if (businessUnitFacilities.sizeT) {
            sizeSystemName = 'B02';
            sizeMm = businessUnitFacilities.sizeT;
          } else if (businessUnitFacilities.diameter) {
            sizeSystemName = 'B03';
            sizeMm = businessUnitFacilities.diameter;
          }
          const sizeFilterboxItem = getProgramOptionFilterboxDataWithSystemName(
            SIZE_NAME,
            sizeSystemName
          );
          const processingEnvironmentsFilterboxItem: DataFilterboxItem[] = [];
          (businessUnitFacilities.processingEnvironments ?? []).map((v) => {
            const item = convertNameOptionToFilterboxItem(v);
            if (item) {
              processingEnvironmentsFilterboxItem.push(item);
            }
          });
          setBusinessUnitBranch([branchFilterboxItem]);
          setCategorys(categoryFilterboxItems);
          setManufacture(businessUnitFacilities.manufacture ?? '');
          setModelNumber(businessUnitFacilities.modelNumber ?? '');
          setAssetsAmount(
            convertLongToString(businessUnitFacilities.assetsAmount)
          );
          setAccuracy(accuracyFilterboxItem ? [accuracyFilterboxItem] : []);
          setProcessableTon(
            convertLongToString(businessUnitFacilities.processableTon)
          );
          setSizeX(convertLongToString(businessUnitFacilities.sizeX));
          setSizeY(convertLongToString(businessUnitFacilities.sizeY));
          setSize(sizeFilterboxItem);
          setSizeMM(convertLongToString(sizeMm));
          setProcessingEnvironments(processingEnvironmentsFilterboxItem);
          setDedicatedMachine(businessUnitFacilities.dedicatedMachine ?? false);
          setRemarks(businessUnitFacilities.remarks ?? '');

          // 編集モードのみ
          if (actionType === 'edit') {
            setNowBusinessUnitBranch([branchFilterboxItem]);
            setNowCategorys(categoryFilterboxItems);
            setNowManufacture(businessUnitFacilities.manufacture ?? '');
            setNowModelNumber(businessUnitFacilities.modelNumber ?? '');
            setNowAssetsAmount(
              convertLongToString(businessUnitFacilities.assetsAmount)
            );
            setNowAccuracy(
              accuracyFilterboxItem ? [accuracyFilterboxItem] : []
            );
            setNowProcessableTon(
              convertLongToString(businessUnitFacilities.processableTon)
            );
            setNowSizeX(convertLongToString(businessUnitFacilities.sizeX));
            setNowSizeY(convertLongToString(businessUnitFacilities.sizeY));
            setNowSize(sizeFilterboxItem);
            setNowSizeMM(convertLongToString(sizeMm));
            setNowProcessingEnvironments(processingEnvironmentsFilterboxItem);
            setNowDedicatedMachine(
              businessUnitFacilities.dedicatedMachine ?? false
            );
            setNowRemarks(businessUnitFacilities.remarks ?? '');

            setLastUpdateInfo({
              isVisibleUpdateInfo: true,
              attribute:
                businessUnitFacilities?.updatedProperties?.updatedBy?.email ??
                '',
              attributeAt:
                businessUnitFacilities?.updatedProperties?.updatedAt ??
                undefined,
              content:
                businessUnitFacilities?.updatedProperties?.updatedBy
                  ?.displayName ?? '',
            });
          }
          // 生成結果テーブル
          setMaterialSkillRelationListItems([]);
          setSkillRelationItems(businessUnitFacilities.skillRelations ?? []);

          const relationData = generateMaterialSkillRelationItems(
            attributes,
            businessUnitFacilities.skillRelations ?? []
          );
          setMaterialSkillRelationTableItems(relationData);
        }

        const { presetItem } = await window.App.services.ui.getViewIdPreset(
          VIEW_ID
        );
        setPreset(presetItem);
      } catch (err) {
        error(getExceptionMessage(intl, err));
      } finally {
        hideLoading();
      }
    })();

    // 初回だけ起動させたい処理なのでlintから除外させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const relationKey = (item: SkillRelation) =>
    `${item.materialItemId ?? ''}-${item.technologyItemId ?? ''}`;
  const uniqueRelations = (items: SkillRelation[]) => {
    const map = new Map<string, SkillRelation>();
    items.forEach((item) => {
      const materialItemId = item.materialItemId ?? '';
      const technologyItemId = item.technologyItemId ?? '';
      if (!materialItemId && !technologyItemId) return;
      map.set(`${materialItemId}-${technologyItemId}`, {
        materialItemId,
        technologyItemId,
      });
    });
    return Array.from(map.values());
  };

  // 型ガード: item が ObjectB (末端アイテム) かどうかを判定
  const isBusinessUnitAttributeItem = (
    item: BusinessUnitAttributeCategory | BusinessUnitAttributeItem
  ): item is BusinessUnitAttributeItem => {
    return 'attributeItemId' in item;
  };

  const flattenAndMerge = (
    items: (BusinessUnitAttributeCategory | BusinessUnitAttributeItem)[],
    targetItemId: string,
    parentInfo: Record<string, string> = {}
  ): MergedItem | null => {
    for (const item of items) {
      if (isBusinessUnitAttributeItem(item)) {
        // 末端アイテムの場合
        if (item.attributeItemId === targetItemId) {
          return {
            ...parentInfo,
            itemId: item.attributeItemId,
            displayName: item.displayNameLang?.ja,
          } as MergedItem;
        }
      } else {
        // カテゴリー(ObjectA)の場合
        const currentInfo = {
          ...parentInfo,
          ...(item.attributeCategoryId
            ? {
                [`cat_${item.attributeCategoryId}`]:
                  item.displayNameLang?.ja ?? '',
              }
            : {}),
        };

        if (item.children) {
          const found = flattenAndMerge(
            item.children,
            targetItemId,
            currentInfo
          );
          if (found) return found;
        }
      }
    }
    return null;
  };

  // 分類条件ダイアログの完了処理
  const handleMaterialTreeComplete = (newCondition: ConditionDetail) => {
    if (editInfo.editIndex === undefined) {
      // 追加
      const relationItems: SkillRelation[] = [];
      if (editInfo.type === 'material') {
        newCondition.conditionItems.map((v) => {
          const relationItem: SkillRelation = {
            materialItemId: v.item.id ?? '',
          };
          relationItems.push(relationItem);
        });
      }
      if (editInfo.type === 'processingSkil') {
        newCondition.conditionItems.map((v) => {
          const relationItem: SkillRelation = {
            technologyItemId: v.item.id ?? '',
          };
          relationItems.push(relationItem);
        });
      }

      const relationData = generateMaterialSkillRelationItems(
        businessUnitAttributesInfo.current,
        relationItems
      );
      // 既存のデータに追加して保存
      setSkillRelationItems([...skillRelationItems, ...relationItems]);
      setMaterialSkillRelationTableItems([
        ...materialSkillRelationTableItems,
        ...relationData,
      ]);
    } else {
      // 編集
      const relationItems: SkillRelation[] = [];
      if (editInfo.type === 'material') {
        (editInfo.relationIds ?? []).map((id) => {
          if (newCondition.conditionItems.length > 0) {
            newCondition.conditionItems.map((v) => {
              const relationItem: SkillRelation = {
                materialItemId: v.item.id ?? '',
                technologyItemId: id.technologyItemId ?? '',
              };
              relationItems.push(relationItem);
            });
          } else {
            const relationItem: SkillRelation = {
              // materialItemId: '',
              technologyItemId: id.technologyItemId ?? '',
            };
            relationItems.push(relationItem);
          }
        });
      }
      if (editInfo.type === 'processingSkil') {
        (editInfo.relationIds ?? []).map((id) => {
          if (newCondition.conditionItems.length > 0) {
            newCondition.conditionItems.map((v) => {
              const relationItem: SkillRelation = {
                materialItemId: id.materialItemId ?? '',
                technologyItemId: v.item.id ?? '',
              };
              relationItems.push(relationItem);
            });
          } else {
            const relationItem: SkillRelation = {
              materialItemId: id.materialItemId ?? '',
              // technologyItemId: '',
            };
            relationItems.push(relationItem);
          }
        });
      }
      // 編集対象のみ置き換える（既存から旧対象を除外し、新対象を追加した後に重複排除）
      const newScopeItems = uniqueRelations(relationItems);
      const targetKeySet = new Set(
        (editInfo.relationIds ?? []).map((v) => relationKey(v))
      );
      const keepItems = skillRelationItems.filter(
        (v) => !targetKeySet.has(relationKey(v))
      );
      const finalItem = uniqueRelations([...keepItems, ...newScopeItems]);

      const relationData = generateMaterialSkillRelationItems(
        businessUnitAttributesInfo.current,
        finalItem
      );
      setSkillRelationItems(finalItem);
      setMaterialSkillRelationTableItems(relationData);
    }
  };

  const renderArrowIcon = (
    target: string,
    value: string | boolean | DataFilterboxItem[]
  ) => {
    return (
      <IconButton
        iconType="arrow_right"
        buttonType="basic"
        caption="コピー"
        name=""
        onClick={() => setChangeItem(target, value)}
      />
    );
  };

  const setChangeItem = (
    target: string,
    value: string | boolean | DataFilterboxItem[]
  ) => {
    switch (target) {
      case 'businessUnitBranch':
        return setBusinessUnitBranch(value as DataFilterboxItem[]);
      case 'categorys':
        return setCategorys(value as DataFilterboxItem[]);
      case 'manufacture':
        return setManufacture(value as string);
      case 'modelNumber':
        return setModelNumber(value as string);
      case 'assetsAmount':
        return setAssetsAmount(value as string);
      case 'accuracy':
        return setAccuracy(value as DataFilterboxItem[]);
      case 'processableTon':
        return setProcessableTon(value as string);
      case 'sizeX':
        return setSizeX(value as string);
      case 'sizeY':
        return setSizeY(value as string);
      case 'size':
        return setSize(value as DataFilterboxItem[]);
      case 'sizeMM':
        return setSizeMM(value as string);
      case 'processingEnvironments':
        return setProcessingEnvironments(value as DataFilterboxItem[]);
      case 'dedicatedMachine':
        return setDedicatedMachine(value as boolean);
      case 'remarks':
        return setRemarks(value as string);
      default:
        console.info(`unknown setter type:[${target}]`);
        return;
    }
  };

  const renderMaterialSkillTable = () => {
    return materialSkillRelationTableItems.map((row, i) => (
      <tr key={i}>
        <th className="width-icon table-center border">
          <IconButton
            iconType="edit"
            buttonType="basic"
            name=""
            caption="編集"
            onClick={() => {
              const items: ConditionItem[] = [];
              row.materialCheckItems.map((v) => {
                items.push({
                  checked: true,
                  item: v,
                });
              });

              const condition: ConditionDetail = {
                multipleType: 'and',
                conditionItems: items,
              };
              const targetMaterial: CheckItem[] =
                row.materialCheckItems.length > 0
                  ? row.materialCheckItems
                  : [{ id: undefined }];
              const targetSkill: CheckItem[] =
                row.skillCheckItems.length > 0
                  ? row.skillCheckItems
                  : [{ id: undefined }];

              const relations = targetSkill.flatMap((sItem) =>
                targetMaterial.map((mItem) => ({
                  materialItemId: mItem.id,
                  technologyItemId: sItem.id,
                }))
              );
              handleMaterialCheckEdit(
                'material',
                materialData,
                i,
                relations,
                condition
              );
            }}
          />
        </th>
        {row.isMaterialFirst && (
          <td className="width-8 border" rowSpan={row.materialSpan}>
            {row.materialCategoryName}
          </td>
        )}
        <td className="width-content border">{row.categoryName}</td>
        <td className="width-content border">{row.materialNames}</td>
        {row.isSkillFirst && (
          <>
            <th
              className="table-center width-icon border"
              rowSpan={row.skillSpan}
            >
              <IconButton
                iconType="edit"
                buttonType="basic"
                name=""
                onClick={() => {
                  const items: ConditionItem[] = [];
                  row.skillCheckItems.map((v) => {
                    items.push({
                      checked: true,
                      item: v,
                    });
                  });

                  const condition: ConditionDetail = {
                    multipleType: 'and',
                    conditionItems: items,
                  };
                  const targetMaterial: CheckItem[] =
                    row.skillTargetMaterialCheckItems.length > 0
                      ? row.skillTargetMaterialCheckItems
                      : [{ id: undefined }];
                  const targetSkill: CheckItem[] =
                    row.skillCheckItems.length > 0
                      ? row.skillCheckItems
                      : [{ id: undefined }];

                  const relations = targetMaterial.flatMap((mItem) =>
                    targetSkill.map((sItem) => ({
                      materialItemId: mItem.id,
                      technologyItemId: sItem.id,
                    }))
                  );

                  handleMaterialCheckEdit(
                    'processingSkil',
                    processingSkillData,
                    i,
                    relations,
                    condition
                  );
                }}
              />
            </th>
            <td rowSpan={row.skillSpan}>{row.skillNames}</td>
          </>
        )}
      </tr>
    ));
  };

  return (
    <>
      <Container viewId={VIEW_ID}>
        <div className="FacilitiesInput">
          <div className="header">
            <PageNavigation
              backpagePath="/facilities-list"
              pageInfo={{
                isVisibleMoveNavi: false,
              }}
              infoOption={{
                lastUpdateInfo: lastUpdateInfo,
                issuerInfo: { isVisibleIssuerInfo: false },
              }}
              handleBackPage={() => setBackConfirmOpen(true)}
            />
          </div>
          <div className="scroll-main-contents-area" ref={formAreaRef}>
            {/* 説明文 */}
            <div className="description-area">
              <p className="text-box">
                <span className="text">
                  {GetMessageWithIntl(intl, {
                    prefixId: VIEW_ID,
                    id: 'noticeInfo',
                  })}
                </span>
              </p>
            </div>

            {/* 生成して登録 */}
            <Accordion
              title={GetMessageWithIntl(intl, {
                id: 'create_title',
                viewId: VIEW_ID,
              })}
            >
              <div className="input-line checkbox-line">
                <div className="item-group-100">
                  <div className="w-75">
                    <Textarea
                      name="promptSample"
                      className="w-100 mh-middle"
                      value={GetMessageWithIntl(intl, {
                        id: 'promptSampleValue',
                        prefixId: VIEW_ID,
                      })}
                      labelId={`${VIEW_ID}.promptSample`}
                      columns={preset.columns}
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-75">
                    <Textarea
                      name="prompt"
                      className="w-100 mh-middle"
                      value={prompt}
                      columns={preset.columns}
                      onChangeState={setPrompt}
                    />
                  </div>
                </div>
              </div>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-75">
                    <CaptionButton
                      name="generateButton"
                      buttonType="basic"
                      caption={labelGenerate}
                      onClick={() => {
                        setGenerateConfirmOpen(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            </Accordion>
            {/* 基本情報 */}
            <Accordion
              title={GetMessageWithIntl(intl, {
                id: 'profile',
                viewId: VIEW_ID,
              })}
            >
              <h4>
                {GetMessageWithIntl(intl, {
                  id: 'businessUnitBranchInfo',
                  viewId: VIEW_ID,
                })}
              </h4>
              <table className="basic-info-table line-bottom">
                <tbody>
                  <tr>
                    <th className="root-header width-title">
                      {GetMessageWithIntl(intl, {
                        id: 'item',
                        viewId: VIEW_ID,
                      })}
                    </th>
                    <th className="root-header width-content" colSpan={2}>
                      {GetMessageWithIntl(intl, {
                        id: 'setting',
                        viewId: VIEW_ID,
                      })}
                    </th>
                    <th className="root-header width-content">
                      {GetMessageWithIntl(intl, {
                        id: 'generateResult',
                        viewId: VIEW_ID,
                      })}
                    </th>
                    <th className="root-header width-title">
                      {GetMessageWithIntl(intl, {
                        id: 'item',
                        viewId: VIEW_ID,
                      })}
                    </th>
                    <th className="root-header width-content" colSpan={2}>
                      {GetMessageWithIntl(intl, {
                        id: 'setting',
                        viewId: VIEW_ID,
                      })}
                    </th>
                    <th className="root-header width-content">
                      {GetMessageWithIntl(intl, {
                        id: 'generateResult',
                        viewId: VIEW_ID,
                      })}
                    </th>
                  </tr>

                  <tr>
                    <td>
                      {/* 設備拠点 */}
                      {GetMessageWithIntl(intl, {
                        id: 'businessUnitBranch',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">
                      {nowBusinessUnitBranch.length > 0
                        ? nowBusinessUnitBranch[0].displayName
                        : ''}
                    </td>
                    <td className="td-right width-icon">
                      {renderArrowIcon(
                        'businessUnitBranch',
                        nowBusinessUnitBranch
                      )}
                    </td>
                    <td>
                      <Filterbox
                        name="businessUnitBranchId"
                        fullMethodName={FullMethodName_ListBusinessUnitBranchs}
                        columns={preset.columns}
                        value={
                          businessUnitBranch ?? [{ value: '', displayName: '' }]
                        }
                        searchOption={{
                          targets: 'displayName',
                          customQuery: {
                            companyId: {
                              $eq: myCompanyInfo.current?.companyId ?? '',
                            },
                          },
                        }}
                        itemType={branchItemType}
                        isNoErrorMessage={true}
                        onChangeState={setBusinessUnitBranch}
                      />
                    </td>
                    <td className="td-border-left">
                      {/* 加工トン数（トン） */}
                      {GetMessageWithIntl(intl, {
                        id: 'processableTon',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">{nowProcessableTon}</td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('processableTon', nowProcessableTon)}
                    </td>
                    <td>
                      <Textbox
                        name="processableTon"
                        value={processableTon}
                        className="right"
                        type="text"
                        columns={preset.columns}
                        isNoErrorMessage={true}
                        onChangeState={setProcessableTon}
                        formatter={CommaTypeFormatter}
                        validator={validateOnlyNumberWithComma(intl)}
                      />
                    </td>
                  </tr>
                  <tr>
                    {/* 設備分類 */}
                    <td>
                      {GetMessageWithIntl(intl, {
                        id: 'categorys',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">
                      {nowCategorys.length > 0
                        ? nowCategorys.map((v) => v.displayName).join('、')
                        : ''}
                    </td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('categorys', nowCategorys)}
                    </td>
                    <td>
                      <DataFilterbox
                        data={facilityCategorys}
                        searchOption={{ targets: 'displayName' }}
                        name="categorys"
                        columns={preset.columns}
                        onChangeState={setCategorys}
                        multiple={true}
                        value={categorys}
                        isNoErrorMessage={true}
                      ></DataFilterbox>
                    </td>
                    <td className="td-border-left">
                      {/* 加工サイズ 幅X（mm） */}
                      {GetMessageWithIntl(intl, {
                        id: 'sizeX',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">{nowSizeX}</td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('sizeX', nowSizeX)}
                    </td>
                    <td>
                      <Textbox
                        name="sizeX"
                        value={sizeX}
                        className="right"
                        type="text"
                        columns={preset.columns}
                        onChangeState={setSizeX}
                        isNoErrorMessage={true}
                        formatter={CommaTypeFormatter}
                        validator={validateOnlyNumberWithComma(intl)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      {/* メーカー */}
                      {GetMessageWithIntl(intl, {
                        id: 'manufacture',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">{nowManufacture}</td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('manufacture', nowManufacture)}
                    </td>
                    <td>
                      <Textbox
                        name="manufacture"
                        value={manufacture}
                        type="text"
                        columns={preset.columns}
                        isNoErrorMessage={true}
                        onChangeState={setManufacture}
                      />
                    </td>
                    <td className="td-border-left">
                      {/* 加工サイズ 奥行きY（mm） */}
                      {GetMessageWithIntl(intl, {
                        id: 'sizeY',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">{nowSizeY}</td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('sizeY', nowSizeY)}
                    </td>
                    <td>
                      <Textbox
                        name="sizeY"
                        value={sizeY}
                        className="right"
                        type="text"
                        columns={preset.columns}
                        isNoErrorMessage={true}
                        onChangeState={setSizeY}
                        formatter={CommaTypeFormatter}
                        validator={validateOnlyNumberWithComma(intl)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      {/* 型番 */}
                      {GetMessageWithIntl(intl, {
                        id: 'modelNumber',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">{nowModelNumber}</td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('modelNumber', nowModelNumber)}
                    </td>
                    <td>
                      <Textbox
                        name="modelNumber"
                        value={modelNumber}
                        type="text"
                        columns={preset.columns}
                        isNoErrorMessage={true}
                        onChangeState={setModelNumber}
                      />
                    </td>
                    <td className="td-border-left">
                      {/* 加工サイズ */}
                      {GetMessageWithIntl(intl, {
                        id: 'size',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">
                      {nowSize.length > 0 ? nowSize[0].displayName : ''}
                    </td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('size', nowSize)}
                    </td>
                    <td>
                      <DataFilterbox
                        data={sizeDatas}
                        searchOption={{ targets: 'displayName' }}
                        name="size"
                        columns={preset.columns}
                        onChangeState={setSize}
                        value={size}
                        isNoErrorMessage={true}
                      ></DataFilterbox>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      {/* 台数 */}
                      {GetMessageWithIntl(intl, {
                        id: 'assetsAmount',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">{nowAssetsAmount}</td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('assetsAmount', nowAssetsAmount)}
                    </td>
                    <td>
                      <Textbox
                        name="assetsAmount"
                        value={assetsAmount}
                        className="right"
                        type="text"
                        columns={preset.columns}
                        isNoErrorMessage={true}
                        onChangeState={setAssetsAmount}
                        formatter={CommaTypeFormatter}
                        validator={validateOnlyNumberWithComma(intl)}
                      />
                    </td>
                    <td className="td-border-left">
                      {/* 加工サイズ（mm） */}
                      {GetMessageWithIntl(intl, {
                        id: 'sizeMM',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">{nowSizeMM}</td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('sizeMM', nowSizeMM)}
                    </td>
                    <td>
                      <Textbox
                        name="sizeMM"
                        value={sizeMM}
                        className="right"
                        type="text"
                        columns={preset.columns}
                        isNoErrorMessage={true}
                        onChangeState={setSizeMM}
                        formatter={CommaTypeFormatter}
                        validator={validateOnlyNumberWithComma(intl)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      {/* 精度 */}
                      {GetMessageWithIntl(intl, {
                        id: 'accuracy',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">
                      {nowAccuracy.length > 0 ? nowAccuracy[0].displayName : ''}
                    </td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('accuracy', nowAccuracy)}
                    </td>
                    <td>
                      <DataFilterbox
                        data={accuracys}
                        searchOption={{ targets: 'displayName' }}
                        name="accuracy"
                        columns={preset.columns}
                        onChangeState={setAccuracy}
                        value={accuracy}
                        isNoErrorMessage={true}
                      ></DataFilterbox>
                    </td>
                    <td className="td-border-left">
                      {/* 加工環境 */}
                      {GetMessageWithIntl(intl, {
                        id: 'processingEnvironments',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">
                      {nowProcessingEnvironments.length > 0
                        ? nowProcessingEnvironments[0].displayName
                        : ''}
                    </td>
                    <td className="td-right width-icon">
                      {renderArrowIcon(
                        'processingEnvironments',
                        nowProcessingEnvironments
                      )}
                    </td>
                    <td>
                      <DataFilterbox
                        data={processingEnvironmentDatas}
                        searchOption={{ targets: 'displayName' }}
                        name="processingEnvironments"
                        columns={preset.columns}
                        onChangeState={setProcessingEnvironments}
                        multiple={true}
                        value={processingEnvironments}
                        isNoErrorMessage={true}
                      ></DataFilterbox>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      {/* 専用機 */}
                      {GetMessageWithIntl(intl, {
                        id: 'dedicatedMachine',
                        viewId: VIEW_ID,
                      })}
                    </td>
                    <td className="td-border-left">
                      <Checkbox
                        name="dedicatedMachine"
                        className="original"
                        columns={preset.columns}
                        items={[
                          {
                            displayName: ' ',
                            value: '1',
                          },
                        ]}
                        value={nowDedicatedMachine ? '1' : ''}
                        disabled={true}
                        isNoErrorMessage={true}
                      />
                    </td>
                    <td className="td-right width-icon">
                      {renderArrowIcon('dedicatedMachine', nowDedicatedMachine)}
                    </td>
                    <td>
                      <Checkbox
                        name="dedicatedMachine"
                        className="original"
                        columns={preset.columns}
                        items={[
                          {
                            displayName: ' ',
                            value: '1',
                          },
                        ]}
                        value={dedicatedMachine ? '1' : ''}
                        onChangeState={handleChangeDedicatedMachine}
                        isNoErrorMessage={true}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="input-line checkbox-line">
                <div className="item-group-100">
                  <div className="w-33">
                    <Textarea
                      name="nowRemarks"
                      className="w-100 mh-middle"
                      value={nowRemarks}
                      labelId={`${VIEW_ID}.nowRemarks`}
                      columns={preset.columns}
                      disabled={true}
                    />
                  </div>
                  <div
                    style={{
                      width: '40px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {renderArrowIcon('remarks', nowRemarks)}
                  </div>
                  <div className="w-33">
                    <Textarea
                      name="remarks"
                      className="w-100 mh-middle"
                      labelId={`${VIEW_ID}.remarks`}
                      value={remarks}
                      columns={preset.columns}
                      onChangeState={setRemarks}
                    />
                  </div>
                </div>
              </div>
            </Accordion>
            {/* 材料-技術 */}
            <Accordion
              title={GetMessageWithIntl(intl, {
                id: 'materialSkill',
                viewId: VIEW_ID,
              })}
            >
              <div className="material-area line-bottom">
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-100">
                      <h4>
                        {GetMessageWithIntl(intl, {
                          id: 'materialSkillDescription',
                          viewId: VIEW_ID,
                        })}
                      </h4>
                    </div>
                  </div>
                </div>
                <div className="input-line line-bottom">
                  <div className="item-group-100">
                    <div className="w-100">
                      <SimpleListView
                        data={materialSkillRelationListItems}
                        viewOptions={{
                          previewRowCount: 5,
                          keyColumn: 'id',
                          columns: materialSkillListColumns,
                        }}
                        actionOptions={{
                          onDelete: handleRemoveConfirm,
                          onDeleteAll: handleAllRemoveConfirmation,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-100">
                      <CaptionButton
                        name="AddMaterialSkillButton"
                        buttonType="basic"
                        caption={labelAddMaterialSkill}
                        onClick={() => {
                          const skillRelations: SkillRelation[] = [];
                          materialSkillRelationListItems.map((v) => {
                            skillRelations.push({
                              materialItemId: v.materialItemId,
                              technologyItemId: v.technologyItemId,
                            });
                          });
                          const relationData =
                            generateMaterialSkillRelationItems(
                              businessUnitAttributesInfo.current,
                              skillRelations
                            );
                          setMaterialSkillRelationTableItems([
                            ...materialSkillRelationTableItems,
                            ...relationData,
                          ]);
                          setSkillRelationItems([
                            ...skillRelationItems,
                            ...skillRelations,
                          ]);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-50">
                    <IconButton
                      iconType="noteadd"
                      buttonType="basic"
                      caption="追加"
                      name=""
                      onClick={() => {
                        setShowMaterialSelectDialog(true);
                      }}
                    />

                    <IconButton
                      iconType="refresh"
                      buttonType="basic"
                      caption="更新"
                      name=""
                      onClick={() => {
                        const relationData = generateMaterialSkillRelationItems(
                          businessUnitAttributesInfo.current,
                          skillRelationItems
                        );
                        setMaterialSkillRelationTableItems(relationData);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="input-line line-bottom">
                <div className="item-group-100">
                  <div className="w-100">
                    <table className="basic-info-table">
                      <tbody>
                        <tr>
                          <th
                            className="root-header width-harf border"
                            colSpan={4}
                          >
                            {GetMessageWithIntl(intl, {
                              id: 'material',
                              viewId: VIEW_ID,
                            })}
                          </th>
                          <th
                            className="root-header width-harf border"
                            colSpan={2}
                          >
                            {GetMessageWithIntl(intl, {
                              id: 'skill',
                              viewId: VIEW_ID,
                            })}
                          </th>
                        </tr>
                        {renderMaterialSkillTable()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </Accordion>
          </div>
          <div className="footer" ref={footerRef}>
            <div className="footer-contents">
              <div className="input-line">
                <CaptionButton
                  name="saveButton"
                  buttonType="basic"
                  caption={labelSave}
                  onClick={() => {
                    if (isInputError()) {
                      return;
                    }
                    setSaveConfirmOpen(true);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <Toast />
      </Container>
      {/* 材料技術追加ダイアログ */}
      <RadioSelectDialog
        isOpen={isShowMaterialSelectDialog}
        inputOption={{
          radioButtonInfo: attributeCategoryRadioItems,
          initialValue: materialSkillValues[0],
        }}
        messageOption={{
          headerMessageOption: {
            id: 'selectAttributeDialog',
            prefixId: 'DIALOG_TITLE',
          },
          descriptionMessageOption: {
            id: 'selectAttributeDialog',
            prefixId: 'DIALOG_CAPTION',
          },
        }}
        onDecision={(v) => {
          const type = v === 'material' ? 'material' : 'processingSkil';
          const selectData =
            v === 'material' ? materialData : processingSkillData;
          handleMaterialCheckAdd(type, selectData);
          setShowMaterialSelectDialog(false);
        }}
        onCancel={() => {
          setShowMaterialSelectDialog(false);
        }}
      />

      {/* 戻る 確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isBackConfirmOpen}
        viewMessage={viewMessageBack}
        onDecision={() => {
          backToPreviewPage();
        }}
        onCancel={() => {
          setBackConfirmOpen(false);
        }}
      />
      {/* 保存 確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isSaveConfirmOpen}
        viewMessage={viewMessageSave}
        onDecision={() => {
          handleSave();
        }}
        onCancel={() => {
          setSaveConfirmOpen(false);
        }}
      />
      {/* 保存 生成ダイアログ */}
      <ConfirmationDialog
        isOpen={isGenerateConfirmOpen}
        viewMessage={viewMessageGenerate}
        onDecision={() => {
          handleGenerate();
        }}
        onCancel={() => {
          setGenerateConfirmOpen(false);
        }}
      />
      {/* 削除 確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        viewMessage={viewMessageDelete}
        onDecision={() => {
          handleDelete();
        }}
        onCancel={() => {
          setDeleteConfirmOpen(false);
        }}
      />
      {/* 明細削除 確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isListDeleteConfirmOpen}
        viewMessage={viewMessageListDelete}
        onDecision={() => {
          handleRemove();
        }}
        onCancel={() => {
          setListDeleteConfirmOpen(false);
        }}
      />

      {/* ヘッダ削除 確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isListDeleteAllConfirmOpen}
        viewMessage={viewMessageListDeleteAll}
        onDecision={() => {
          handleAllRemove();
        }}
        onCancel={() => {
          setListDeleteAllConfirmOpen(false);
        }}
      />

      <MaterialTreeDialog
        ref={materialTreeDialogRef}
        messageOption={{
          headerLabelId: { prefixId: VIEW_ID, id: `select_${editInfo.type}` },
          labelEveryMatch: {
            viewId: VIEW_ID,
            id: 'every_match',
          },
          labelSomeMatch: {
            viewId: VIEW_ID,
            id: 'some_match',
          },
        }}
        onDecision={handleMaterialTreeComplete}
      />
    </>
  );
};
